import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp, TrendingDown, Activity } from 'lucide-react';
import useLivePrices from '../hooks/useLivePrices';

const BtcIcon = () => (
  <svg viewBox="0 0 32 32" className="w-4 h-4">
    <circle cx="16" cy="16" r="16" fill="#f7931a" />
    <path d="M22.5 14.2c.3-2-1.2-3-3.3-3.8l.7-2.7-1.6-.4-.6 2.6c-.4-.1-.9-.2-1.3-.3l.7-2.6-1.7-.4-.7 2.7c-.4-.1-.7-.2-1-.2l-2.3-.6-.4 1.7s1.2.3 1.2.3c.7.2.8.6.8 1l-.8 3.2c0 0 .1 0 .2.1h-.2l-1.1 4.5c-.1.2-.3.5-.7.4 0 0-1.2-.3-1.2-.3l-.8 1.9 2.2.5c.4.1.8.2 1.2.3l-.7 2.7 1.6.4.7-2.7c.4.1.9.2 1.3.3l-.7 2.7 1.7.4.7-2.7c2.8.5 4.9.3 5.8-2.2.7-2-.1-3.2-1.5-3.9 1.1-.3 1.9-1 2.1-2.5zm-3.7 5.2c-.5 2-4 .9-5.1.7l.9-3.7c1.1.3 4.7.8 4.2 3zm.5-5.3c-.5 1.8-3.3.9-4.3.7l.8-3.3c1 .2 4 .7 3.5 2.6z" fill="white"/>
  </svg>
);

const EthIcon = () => (
  <svg viewBox="0 0 32 32" className="w-4 h-4">
    <circle cx="16" cy="16" r="16" fill="#627eea" />
    <path d="M16.5 4v8.9l7.5 3.3L16.5 4z" fill="white" opacity="0.6"/>
    <path d="M16.5 4L9 16.2l7.5-3.3V4z" fill="white"/>
    <path d="M16.5 21.9v6.1l7.5-10.4-7.5 4.3z" fill="white" opacity="0.6"/>
    <path d="M16.5 28v-6.1L9 17.6l7.5 10.4z" fill="white"/>
    <path d="M16.5 20.6l7.5-4.4-7.5-3.3v7.7z" fill="white" opacity="0.2"/>
    <path d="M9 16.2l7.5 4.4v-7.7L9 16.2z" fill="white" opacity="0.6"/>
  </svg>
);

const AaveIcon = () => (
  <svg viewBox="0 0 32 32" className="w-4 h-4">
    <circle cx="16" cy="16" r="16" fill="#B6509E" />
    <path d="M16 6 C11 6 8 10 8 14 C8 17 9.5 19.5 12 21 L10.5 25 L13 25 L14 22.5 C14.6 22.7 15.3 22.8 16 22.8 C16.7 22.8 17.4 22.7 18 22.5 L19 25 L21.5 25 L20 21 C22.5 19.5 24 17 24 14 C24 10 21 6 16 6 Z M16 8.5 C19.5 8.5 21.5 11 21.5 14 C21.5 17 19.5 20.3 16 20.3 C12.5 20.3 10.5 17 10.5 14 C10.5 11 12.5 8.5 16 8.5 Z" fill="white"/>
  </svg>
);

const EurIcon = () => (
  <svg viewBox="0 0 32 32" className="w-4 h-4">
    <circle cx="16" cy="16" r="16" fill="#3b82f6" />
    <text x="16" y="22" textAnchor="middle" fill="white" fontSize="16" fontWeight="bold">€</text>
  </svg>
);

const DogeIcon = () => (
  <svg viewBox="0 0 32 32" className="w-4 h-4">
    <circle cx="16" cy="16" r="16" fill="#C2A633" />
    <path d="M11 9h5.5C19.5 9 22 11.5 22 16C22 20.5 19.5 23 16.5 23H11V9Z M13.5 11.5V20.5H16C18 20.5 19.5 18.8 19.5 16C19.5 13.2 18 11.5 16 11.5H13.5Z" fill="white"/>
    <line x1="10" y1="15" x2="18" y2="15" stroke="white" strokeWidth="2"/>
  </svg>
);

const AlgoIcon = () => (
  <svg viewBox="0 0 32 32" className="w-4 h-4">
    <circle cx="16" cy="16" r="16" fill="#222222" />
    <path d="M10 24 L14.5 10 L17 10 L19 17 L21 12 L23.5 12 L20 24 L17.5 24 L15.5 17 L13.5 24 Z" fill="white"/>
  </svg>
);

const SolIcon = () => (
  <svg viewBox="0 0 32 32" className="w-4 h-4">
    <circle cx="16" cy="16" r="16" fill="#9945FF" />
    <path d="M9 20.5h11.5l-1.5 2H9v-2zm0-5.5h11.5l-1.5 2H9v-2zm3-5.5h8.5l-1.5 2H9l3-2z" fill="white"/>
  </svg>
);

const assetIcons = {
  'BTC/USD': BtcIcon,
  'ETH/USD': EthIcon,
  'SOL/USD': SolIcon,
  'AAVE/USD': AaveIcon,
  'EUR/USD':  EurIcon,
  'DOGE/USD': DogeIcon,
  'ALGO/USD': AlgoIcon,
};

const MiniChart = ({ data, color = '#38FE60', height = 32 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const w = rect.width;
    const h = rect.height;

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const xStep = w / (data.length - 1);

    const points = data.map((v, i) => ({
      x: i * xStep,
      y: h - ((v - min) / range) * h * 0.8 - h * 0.1,
    }));

    
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, color + '25');
    grad.addColorStop(1, color + '00');
    ctx.beginPath();
    ctx.moveTo(0, h);
    points.forEach((p, i) => {
      if (i === 0) ctx.lineTo(p.x, p.y);
      else {
        const prev = points[i - 1];
        const cpx = (prev.x + p.x) / 2;
        ctx.quadraticCurveTo(cpx, prev.y, cpx, (prev.y + p.y) / 2);
        ctx.quadraticCurveTo(cpx, p.y, p.x, p.y);
      }
    });
    ctx.lineTo(w, h);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();

    
    ctx.beginPath();
    points.forEach((p, i) => {
      if (i === 0) ctx.moveTo(p.x, p.y);
      else {
        const prev = points[i - 1];
        const cpx = (prev.x + p.x) / 2;
        ctx.quadraticCurveTo(cpx, prev.y, cpx, (prev.y + p.y) / 2);
        ctx.quadraticCurveTo(cpx, p.y, p.x, p.y);
      }
    });
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.stroke();

    
    const last = points[points.length - 1];
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(last.x, last.y, 2, 0, Math.PI * 2);
    ctx.fill();
  }, [data, color, height]);

  return <canvas ref={canvasRef} className="w-full" style={{ height }} />;
};

const ASSET_LIST = ['BTC/USD', 'ETH/USD', 'SOL/USD', 'AAVE/USD', 'DOGE/USD', 'ALGO/USD'];

const formatPrice = (name, price) => {
  if (!price) return '—';
  if (name === 'EUR/USD') return price.toFixed(4);
  if (price >= 1000) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return price.toFixed(2);
};

const HeroVisual = () => {
  const livePrices = useLivePrices();
  const [sparklines, setSparklines] = useState(() => {
    const s = {};
    ASSET_LIST.forEach(name => {
      const d = [];
      let v = 50;
      for (let i = 0; i < 24; i++) { v += (Math.random() - 0.5) * 5; v = Math.max(10, Math.min(90, v)); d.push(v); }
      s[name] = d;
    });
    return s;
  });

  
  useEffect(() => {
    const t = setInterval(() => {
      setSparklines(prev => {
        const next = { ...prev };
        ASSET_LIST.forEach(name => {
          const last = next[name][next[name].length - 1];
          const up = livePrices[name]?.change >= 0;
          const newVal = Math.max(10, Math.min(90, last + (Math.random() - (up ? 0.45 : 0.55)) * 4));
          next[name] = [...next[name].slice(1), newVal];
        });
        return next;
      });
    }, 2000);
    return () => clearInterval(t);
  }, [livePrices]);

  const assets = ASSET_LIST.map(name => {
    const p = livePrices[name] || {};
    return {
      name,
      price: formatPrice(name, p.price),
      change: p.change != null ? (p.change >= 0 ? '+' : '') + p.change.toFixed(2) : '—',
      up: (p.change ?? 0) >= 0,
      loading: p.loading,
      data: sparklines[name] || [],
    };
  });

  return (
    <div className="w-full h-full rounded-2xl overflow-hidden border border-[#1e1e22] flex flex-col shadow-2xl shadow-black/50"
      style={{
        background: 'linear-gradient(180deg, #131316 0%, #0e0e10 100%)',
      }}
    >
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#1e1e22]">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#38FE60]/10 border border-[#38FE60]/20 flex items-center justify-center">
            <Activity className="w-4 h-4 text-[#38FE60]" />
          </div>
          <div>
            <span className="text-[12px] text-white font-semibold tracking-wide">Market Overview</span>
            <div className="text-[9px] text-[#555] font-mono">6 instruments</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-2 py-1 rounded border border-[#38FE60]/15 bg-[#38FE60]/05">
          <div className="w-1.5 h-1.5 rounded-full bg-[#38FE60] animate-pulse" />
          <span className="text-[9px] text-[#38FE60] font-mono">Live</span>
        </div>
      </div>

            <div className="flex items-center px-5 py-2 border-b border-[#1a1a1e] text-[8px] text-[#444] font-mono tracking-wider uppercase">
        <div className="flex-1">Asset</div>
        <div className="w-[72px] text-center">Chart</div>
        <div className="w-[90px] text-right">Price</div>
      </div>

            <div className="flex-1 overflow-hidden">
        {assets.map((asset, i) => {
          const IconComp = assetIcons[asset.name];
          return (
            <div
              key={asset.name}
              className="flex items-center px-5 py-[10px] border-b border-[#ffffff04] hover:bg-[#ffffff04] transition-all duration-300 hero-asset-row group"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110 bg-[#ffffff06]">
                  {IconComp && <IconComp />}
                </div>
                <div>
                  <div className="text-white text-[12px] font-semibold">{asset.name}</div>
                </div>
              </div>

                            <div className="w-[72px] flex-shrink-0 mx-2">
                <MiniChart
                  data={asset.data}
                  color='#38FE60'
                  height={30}
                />
              </div>

                            <div className="text-right flex-shrink-0 w-[90px]">
                <div className="text-white text-[12px] font-semibold font-mono">${asset.price}</div>
                <div className="text-[10px] font-medium flex items-center justify-end gap-0.5 mt-0.5 text-[#38FE60]">
                  <TrendingUp className="w-3 h-3" />
                  <span className="font-mono">{asset.change}%</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

            <div className="px-5 py-3 border-t border-[#1e1e22] flex items-center justify-between"
        style={{ background: 'linear-gradient(180deg, #131316 0%, #111113 100%)' }}
      >
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#38FE60]"></div>
            <span className="text-[10px] text-[#666] font-mono">Bullish</span>
            <span className="text-[#38FE60] text-xs font-bold font-mono">
              {assets.filter(a => a.up).length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#ff5252]"></div>
            <span className="text-[10px] text-[#666] font-mono">Bearish</span>
            <span className="text-[#ff5252] text-xs font-bold font-mono">
              {assets.filter(a => !a.up).length}
            </span>
          </div>
        </div>
        <span className="text-[9px] text-[#444] font-mono">Binance · Realtime</span>
      </div>
    </div>
  );
};

export default HeroVisual;
